import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ExternalPayments } from '../../components/crm/VisitAndBilling';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import {
  ArrowLeft, Loader2, FileText, Search, DollarSign, Plus, Send, Copy, Ban,
  RefreshCw, ExternalLink, MessageSquare, Mail, ChevronDown, ChevronUp, X, Check, Clock, CalendarClock, Sparkles, Archive as ArchiveIcon, RotateCcw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import RecordActions from '../../components/RecordActions';
import { AdminSubNav, SUBNAV_INVOICES } from '../../components/admin/AdminSubNav';
import InvoiceEditDialog from '../../components/admin/InvoiceEditDialog';
import InvoiceAuditTrail from '../../components/admin/InvoiceAuditTrail';
import { Pencil } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     color: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  sent:      { label: 'Sent',      color: 'bg-blue-50 text-blue-700 border-blue-200' },
  paid:      { label: 'Paid',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  overdue:   { label: 'Overdue',   color: 'bg-red-50 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', color: 'bg-zinc-50 text-zinc-400 border-zinc-200' },
};

const STATUSES = ['all', 'draft', 'sent', 'paid', 'overdue', 'cancelled'];

// ============ MAIN COMPONENT ============

const AdminInvoicesPage = () => {
  const { token, isMasterAdmin } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedInv, setSelectedInv] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchInvoices = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      if (showArchived) params.show_archived = 'true';
      const res = await axios.get(`${API_URL}/api/admin-invoices`, { headers, params });
      setInvoices(res.data);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [headers, statusFilter, search, showArchived]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.charge_amount || i.total), 0);
  const totalPending = invoices.filter(i => ['sent', 'draft'].includes(i.status)).reduce((s, i) => s + (i.charge_amount || i.total), 0);
  const totalOverdue = invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + (i.charge_amount || i.total), 0);

  // Actions
  const sendInvoice = async (inv) => {
    setActionLoading(`send-${inv.id}`);
    try {
      await axios.post(`${API_URL}/api/admin-invoices/${inv.id}/send`, {}, { headers });
      toast.success('Invoice sent');
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setActionLoading(''); }
  };

  const cancelScheduledSend = async (inv) => {
    if (!window.confirm(`Cancel the scheduled email for ${inv.invoice_number}? The draft invoice will stay intact.`)) return;
    setActionLoading(`cancel-sched-${inv.id}`);
    try {
      await axios.post(`${API_URL}/api/admin-invoices/${inv.id}/cancel-scheduled-send`, {}, { headers });
      toast.success('Scheduled send cancelled');
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
    finally { setActionLoading(''); }
  };

  const voidInvoice = async (inv) => {
    setActionLoading(`void-${inv.id}`);
    try {
      await axios.post(`${API_URL}/api/admin-invoices/${inv.id}/void`, {}, { headers });
      toast.success('Invoice cancelled');
      fetchInvoices();
      setDetailOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
    finally { setActionLoading(''); }
  };

  const archiveInvoice = async (inv) => {
    try {
      await axios.post(`${API_URL}/api/admin-invoices/${inv.id}/archive`, {}, { headers });
      toast.success('Invoice archived');
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to archive'); }
  };

  const restoreInvoice = async (inv) => {
    try {
      await axios.post(`${API_URL}/api/admin-invoices/${inv.id}/restore`, {}, { headers });
      toast.success('Invoice restored');
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to restore'); }
  };

  const syncInvoice = async (inv) => {
    setActionLoading(`sync-${inv.id}`);
    try {
      const res = await axios.post(`${API_URL}/api/admin-invoices/${inv.id}/sync`, {}, { headers });
      toast.success(`Status updated: ${res.data.status}`);
      setSelectedInv(res.data);
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to sync'); }
    finally { setActionLoading(''); }
  };

  const copySMS = async (inv) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin-invoices/${inv.id}/sms-message`, { headers });
      await navigator.clipboard.writeText(res.data.message);
      toast.success('SMS message copied');
    } catch { toast.error('Failed to generate SMS'); }
  };

  const copyLink = async (inv) => {
    if (inv.hosted_invoice_url) {
      await navigator.clipboard.writeText(inv.hosted_invoice_url);
      toast.success('Payment link copied');
    } else { toast.error('No payment link available'); }
  };

  // Loading
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
    </div>
  );

  // Create mode
  if (showCreate) return (
    <CreateInvoiceForm
      headers={headers}
      onClose={() => setShowCreate(false)}
      onCreated={() => { setShowCreate(false); fetchInvoices(); }}
    />
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-invoices-page">
      {/* Header */}
      <div className="gg-legacy-head bg-white border-b border-zinc-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="back-btn" className="flex-shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 truncate">Invoices</h1>
              <p className="text-[11px] sm:text-xs text-zinc-500">{invoices.length} total</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl flex-shrink-0 px-3 sm:px-4" data-testid="create-invoice-btn">
            <Plus className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Create Invoice</span>
          </Button>
        </div>
      </div>
      <AdminSubNav section="Invoices" items={SUBNAV_INVOICES} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Paid', amount: totalPaid, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Pending', amount: totalPending, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Overdue', amount: totalOverdue, color: 'text-red-600', bg: 'bg-red-50' },
          ].map(stat => (
            <Card key={stat.label} className="border-zinc-100">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                  <DollarSign className={`h-5 w-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">{stat.label}</p>
                  <p className={`text-lg font-bold ${stat.color}`}>${stat.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Search by name, email, phone, or invoice #..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 rounded-xl border-zinc-200" data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUSES.map(s => (
              <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm"
                className={`rounded-full text-xs capitalize ${statusFilter === s ? 'bg-zinc-900 text-white' : 'border-zinc-200'}`}
                onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}>
                {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
              </Button>
            ))}
            <Button variant={showArchived ? 'default' : 'outline'} size="sm"
              className={`rounded-full text-xs ${showArchived ? 'bg-zinc-900 text-white' : 'border-zinc-200'}`}
              onClick={() => setShowArchived(v => !v)} data-testid="toggle-show-archived">
              <ArchiveIcon className="h-3 w-3 mr-1" /> {showArchived ? 'Hide Archived' : 'Show Archived'}
            </Button>
          </div>
        </div>

        {/* Invoice Table */}
        {invoices.length === 0 && !search ? (
          <Card className="border-zinc-100 border-dashed">
            <CardContent className="py-16 text-center">
              <FileText className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
              <h3 className="font-bold text-zinc-900 mb-1">No invoices yet</h3>
              <p className="text-sm text-zinc-500 mb-4">Create your first invoice to get started</p>
              <Button onClick={() => setShowCreate(true)} className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl">
                <Plus className="h-4 w-4 mr-2" /> Create Invoice
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
          {/* Desktop Table */}
          <Card className="border-zinc-100 overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="invoices-table">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50/50">
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3">Invoice</th>
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3">Client</th>
                    <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3 hidden md:table-cell">Service</th>
                    <th className="text-right text-xs font-semibold text-zinc-500 px-4 py-3">Amount</th>
                    <th className="text-center text-xs font-semibold text-zinc-500 px-4 py-3">Status</th>
                    <th className="text-right text-xs font-semibold text-zinc-500 px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => {
                    const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                    return (
                      <tr key={inv.id} className="border-b border-zinc-50 hover:bg-zinc-50/50 cursor-pointer transition-colors"
                        onClick={() => { setSelectedInv(inv); setDetailOpen(true); }} data-testid={`invoice-row-${inv.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-sm text-zinc-900">{inv.invoice_number}</p>
                          <p className="text-[11px] text-zinc-400">{new Date(inv.created_at).toLocaleDateString()}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-zinc-800">{inv.client_name}</p>
                          <p className="text-[11px] text-zinc-400">{inv.client_email}</p>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <p className="text-sm text-zinc-600">{inv.service_type || '—'}</p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-bold text-sm text-zinc-900">${(inv.charge_amount || inv.total)?.toFixed(2)}</p>
                          {inv.payment_type === 'deposit' && <p className="text-[10px] text-zinc-400">Deposit</p>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`${cfg.color} border text-[10px] font-semibold`}>{cfg.label}</Badge>
                          {inv.archived && (
                            <Badge className="ml-1 bg-zinc-200 text-zinc-700 border-zinc-300 text-[10px]" data-testid={`invoice-archived-badge-${inv.id}`}>Archived</Badge>
                          )}
                          {inv.scheduled_send_status === 'pending' && inv.scheduled_send_at && (
                            <div className="mt-1 text-[10px] text-[#F56F05] font-semibold flex items-center justify-center gap-1" data-testid={`scheduled-pill-${inv.id}`}>
                              <CalendarClock className="h-3 w-3" />
                              {new Date(inv.scheduled_send_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </div>
                          )}
                          {inv.scheduled_send_status === 'failed' && (
                            <div className="mt-1 text-[10px] text-red-600 font-semibold" title={inv.scheduled_send_error}>Auto-send failed</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {inv.hosted_invoice_url && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyLink(inv)} title="Copy payment link" data-testid={`copy-link-${inv.id}`}>
                                <Copy className="h-3.5 w-3.5 text-zinc-400" />
                              </Button>
                            )}
                            {inv.status === 'sent' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copySMS(inv)} title="Copy SMS" data-testid={`copy-sms-${inv.id}`}>
                                <MessageSquare className="h-3.5 w-3.5 text-zinc-400" />
                              </Button>
                            )}
                            {inv.scheduled_send_status === 'pending' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-red-50"
                                onClick={() => cancelScheduledSend(inv)} disabled={actionLoading === `cancel-sched-${inv.id}`}
                                title="Cancel scheduled send" data-testid={`cancel-sched-${inv.id}`}>
                                {actionLoading === `cancel-sched-${inv.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5 text-red-500" />}
                              </Button>
                            )}
                            {['draft', 'sent'].includes(inv.status) && inv.scheduled_send_status !== 'pending' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-orange-50"
                                onClick={() => sendInvoice(inv)} disabled={actionLoading === `send-${inv.id}`}
                                title={inv.status === 'sent' ? 'Resend email' : 'Send email'}
                                data-testid={`send-${inv.id}`}>
                                {actionLoading === `send-${inv.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[#F56F05]" /> : <Send className="h-3.5 w-3.5 text-[#F56F05]" />}
                              </Button>
                            )}
                            {inv.archived ? (
                              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs"
                                onClick={() => restoreInvoice(inv)} data-testid={`invoice-restore-${inv.id}`}>
                                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                              </Button>
                            ) : (
                              <RecordActions
                                canEdit={false}
                                canArchive={true}
                                canDelete={true}
                                deleteUrl={`/api/admin-invoices/${inv.id}`}
                                deleteConfirmTitle={`Delete invoice ${inv.invoice_number}?`}
                                deleteConfirmDescription="Permanently removes the invoice. Only draft (never finalized) or cancelled invoices can be deleted — others must be voided first or archived."
                                onArchive={() => archiveInvoice(inv)}
                                onDeleted={fetchInvoices}
                                headers={headers}
                                testIdPrefix="invoice"
                                entityId={inv.id}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3" data-testid="invoices-cards-mobile">
            {invoices.map(inv => {
              const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
              return (
                <Card key={inv.id} className="border-zinc-100 active:shadow-md transition-shadow" data-testid={`invoice-card-mobile-${inv.id}`}>
                  <CardContent className="p-4">
                    <button
                      onClick={() => { setSelectedInv(inv); setDetailOpen(true); }}
                      className="w-full text-left"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-bold text-sm text-zinc-900">{inv.invoice_number}</p>
                            <Badge className={`${cfg.color} border text-[10px] font-semibold`}>{cfg.label}</Badge>
                            {inv.archived && (
                              <Badge className="bg-zinc-200 text-zinc-700 border-zinc-300 text-[10px]" data-testid={`invoice-archived-badge-mobile-${inv.id}`}>Archived</Badge>
                            )}
                          </div>
                          <p className="text-[11px] text-zinc-400 mt-0.5">{new Date(inv.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-extrabold text-lg text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>${(inv.charge_amount || inv.total)?.toFixed(2)}</p>
                          {inv.payment_type === 'deposit' && <p className="text-[10px] text-zinc-400">Deposit</p>}
                        </div>
                      </div>
                      <div className="text-sm text-zinc-800 truncate">{inv.client_name}</div>
                      <div className="text-[11px] text-zinc-400 truncate">{inv.client_email}</div>
                      {inv.service_type && <div className="text-[11px] text-zinc-500 mt-1 truncate">Service: {inv.service_type}</div>}
                      {inv.scheduled_send_status === 'pending' && inv.scheduled_send_at && (
                        <div className="mt-2 text-[10px] text-[#F56F05] font-semibold flex items-center gap-1" data-testid={`scheduled-pill-mobile-${inv.id}`}>
                          <CalendarClock className="h-3 w-3" />
                          Auto-send {new Date(inv.scheduled_send_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </div>
                      )}
                      {inv.scheduled_send_status === 'failed' && (
                        <div className="mt-2 text-[10px] text-red-600 font-semibold">Auto-send failed</div>
                      )}
                    </button>
                    {/* Action row */}
                    <div className="flex items-center gap-1.5 pt-3 mt-3 border-t border-zinc-100" onClick={e => e.stopPropagation()}>
                      {inv.hosted_invoice_url && (
                        <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => copyLink(inv)} data-testid={`copy-link-mobile-${inv.id}`}>
                          <Copy className="h-3 w-3 mr-1" /> Link
                        </Button>
                      )}
                      {inv.status === 'sent' && (
                        <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => copySMS(inv)} data-testid={`copy-sms-mobile-${inv.id}`}>
                          <MessageSquare className="h-3 w-3 mr-1" /> SMS
                        </Button>
                      )}
                      {inv.scheduled_send_status === 'pending' && (
                        <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200"
                          onClick={() => cancelScheduledSend(inv)} disabled={actionLoading === `cancel-sched-${inv.id}`}
                          data-testid={`cancel-sched-mobile-${inv.id}`}>
                          {actionLoading === `cancel-sched-${inv.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        </Button>
                      )}
                      {['draft', 'sent'].includes(inv.status) && inv.scheduled_send_status !== 'pending' && (
                        <Button size="sm" className="flex-1 h-8 text-xs bg-[#F56F05] hover:bg-[#e06505] text-white"
                          onClick={() => sendInvoice(inv)} disabled={actionLoading === `send-${inv.id}`}
                          data-testid={`send-mobile-${inv.id}`}>
                          {actionLoading === `send-${inv.id}` ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                          {inv.status === 'sent' ? 'Resend' : 'Send'}
                        </Button>
                      )}
                      {inv.archived ? (
                        <Button size="sm" variant="outline" className="h-8 text-xs"
                          onClick={() => restoreInvoice(inv)} data-testid={`invoice-restore-mobile-${inv.id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" /> Restore
                        </Button>
                      ) : (
                        <RecordActions
                          canEdit={false}
                          canArchive={true}
                          canDelete={true}
                          deleteUrl={`/api/admin-invoices/${inv.id}`}
                          deleteConfirmTitle={`Delete invoice ${inv.invoice_number}?`}
                          deleteConfirmDescription="Permanently removes the invoice. Only draft (never finalized) or cancelled invoices can be deleted — others must be voided first or archived."
                          onArchive={() => archiveInvoice(inv)}
                          onDeleted={fetchInvoices}
                          headers={headers}
                          testIdPrefix="invoice-mobile"
                          entityId={inv.id}
                        />
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Detail Modal */}
      <InvoiceDetailModal
        invoice={selectedInv}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onSend={sendInvoice}
        onVoid={voidInvoice}
        onSync={syncInvoice}
        onCopyLink={copyLink}
        onCopySMS={copySMS}
        onEdit={isMasterAdmin ? () => { setDetailOpen(false); setEditOpen(true); } : null}
        headers={headers}
        actionLoading={actionLoading}
      />
      <InvoiceEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        invoice={selectedInv}
        headers={headers}
        onSaved={() => { fetchInvoices(); setEditOpen(false); }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-10"><ExternalPayments headers={headers} invoices={invoices} /></div>
    </div>
  );
};

// ============ INVOICE DETAIL MODAL ============

const InvoiceDetailModal = ({ invoice: inv, open, onClose, onSend, onVoid, onSync, onCopyLink, onCopySMS, onEdit, headers, actionLoading }) => {
  if (!inv) return null;
  const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span>{inv.invoice_number}</span>
            <div className="flex items-center gap-2">
              <Badge className={`${cfg.color} border text-xs`}>{cfg.label}</Badge>
              {onEdit && (
                <Button size="sm" variant="outline" onClick={onEdit} data-testid="detail-edit-invoice">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
              )}
            </div>
          </DialogTitle>
          <DialogDescription>Created {new Date(inv.created_at).toLocaleDateString()}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Client Info */}
          <div className="bg-zinc-50 rounded-xl p-4 space-y-1">
            <p className="font-bold text-sm text-zinc-900">{inv.client_name}</p>
            <p className="text-xs text-zinc-500">{inv.client_email}</p>
            {inv.client_phone && <p className="text-xs text-zinc-500">{inv.client_phone}</p>}
            {inv.property_address && <p className="text-xs text-zinc-400 mt-1">{inv.property_address}</p>}
          </div>

          {/* Service Details */}
          {(inv.service_type || inv.date_of_service) && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {inv.service_type && <div><p className="text-xs text-zinc-400">Service</p><p className="font-medium">{inv.service_type}</p></div>}
              {inv.date_of_service && <div><p className="text-xs text-zinc-400">Date of Service</p><p className="font-medium">{inv.date_of_service}</p></div>}
              {inv.bedrooms && <div><p className="text-xs text-zinc-400">Bedrooms</p><p className="font-medium">{inv.bedrooms}</p></div>}
              {inv.bathrooms && <div><p className="text-xs text-zinc-400">Bathrooms</p><p className="font-medium">{inv.bathrooms}</p></div>}
            </div>
          )}

          <Separator />

          {/* Line Items */}
          <div>
            <p className="text-xs font-semibold text-zinc-500 mb-2 uppercase tracking-wider">Line Items</p>
            <div className="space-y-2">
              {inv.line_items?.map((item, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-zinc-700">{item.description} <span className="text-zinc-400">x{item.quantity}</span></span>
                  <span className="font-medium">${(item.unit_price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <Separator className="my-2" />
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>${inv.subtotal?.toFixed(2)}</span></div>
              {inv.tax_amount > 0 && <div className="flex justify-between"><span className="text-zinc-500">Tax / Fee</span><span>${inv.tax_amount?.toFixed(2)}</span></div>}
              <div className="flex justify-between font-bold text-base pt-1">
                <span>{inv.payment_type === 'deposit' ? `Deposit (${inv.deposit_percentage}%)` : 'Total'}</span>
                <span className="text-[#F56F05]">${(inv.charge_amount || inv.total)?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {inv.notes && (
            <div><p className="text-xs text-zinc-400 mb-1">Client Notes</p><p className="text-sm bg-zinc-50 rounded-lg p-3">{inv.notes}</p></div>
          )}
          {inv.internal_notes && (
            <div><p className="text-xs text-zinc-400 mb-1">Internal Notes</p><p className="text-sm bg-amber-50 rounded-lg p-3 text-amber-800">{inv.internal_notes}</p></div>
          )}

          {/* Audit trail — collapsible */}
          {headers && (
            <InvoiceAuditTrail invoiceId={inv.id} headers={headers} />
          )}

          <Separator />

          {/* Actions */}
          <div className="grid grid-cols-2 gap-2">
            {inv.hosted_invoice_url && (
              <>
                <Button variant="outline" className="rounded-xl text-sm h-10" onClick={() => onCopyLink(inv)} data-testid="detail-copy-link">
                  <Copy className="h-3.5 w-3.5 mr-2" /> Copy Link
                </Button>
                <Button variant="outline" className="rounded-xl text-sm h-10" onClick={() => onCopySMS(inv)} data-testid="detail-copy-sms">
                  <MessageSquare className="h-3.5 w-3.5 mr-2" /> Copy SMS
                </Button>
              </>
            )}
            {['draft', 'sent'].includes(inv.status) && (
              <Button className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl text-sm h-10"
                onClick={() => onSend(inv)} disabled={actionLoading === `send-${inv.id}`} data-testid="detail-send">
                {actionLoading === `send-${inv.id}` ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-2" />}
                {inv.status === 'sent' ? 'Resend' : 'Send'} Email
              </Button>
            )}
            <Button variant="outline" className="rounded-xl text-sm h-10" onClick={() => onSync(inv)}
              disabled={actionLoading === `sync-${inv.id}`} data-testid="detail-sync">
              {actionLoading === `sync-${inv.id}` ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-2" />}
              Sync Status
            </Button>
            {inv.hosted_invoice_url && (
              <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex">
                <Button variant="outline" className="rounded-xl text-sm h-10 w-full" data-testid="detail-view-stripe">
                  <ExternalLink className="h-3.5 w-3.5 mr-2" /> View Invoice
                </Button>
              </a>
            )}
            {inv.status !== 'paid' && inv.status !== 'cancelled' && (
              <Button variant="outline" className="rounded-xl text-sm h-10 text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => onVoid(inv)} disabled={actionLoading === `void-${inv.id}`} data-testid="detail-void">
                {actionLoading === `void-${inv.id}` ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Ban className="h-3.5 w-3.5 mr-2" />}
                Cancel Invoice
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ CREATE INVOICE FORM ============

const CreateInvoiceForm = ({ headers, onClose, onCreated }) => {
  const [templates, setTemplates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showTemplates, setShowTemplates] = useState(true);
  const [form, setForm] = useState({
    client_name: '', client_email: '', client_phone: '',
    service_type: '', property_address: '',
    bedrooms: '', bathrooms: '', square_footage: '',
    date_of_service: '', due_date: '',
    notes: '', internal_notes: '',
    line_items: [{ _key: Date.now(), description: '', quantity: 1, unit_price: 0 }],
    tax_amount: 0, payment_type: 'full', deposit_percentage: 25,
    send_email: true,
    schedule_send: false,
    scheduled_send_at: '',
    apply_referral_discount: false,
  });
  const [referralDiscount, setReferralDiscount] = useState(null);

  // Look up the client's pending referral discount whenever the email changes
  useEffect(() => {
    const email = form.client_email?.trim();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setReferralDiscount(null);
      setForm(prev => prev.apply_referral_discount ? { ...prev, apply_referral_discount: false } : prev);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/referrals/check-discount`, {
          params: { email }, headers,
        });
        if (r.data?.has_discount) {
          setReferralDiscount(r.data);
        } else {
          setReferralDiscount(null);
          setForm(prev => prev.apply_referral_discount ? { ...prev, apply_referral_discount: false } : prev);
        }
      } catch (_e) {
        setReferralDiscount(null);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [form.client_email, headers]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin-invoices/templates`, { headers })
      .then(res => setTemplates(res.data))
      .catch(() => {});
  }, [headers]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const updateLineItem = (index, field, value) => {
    const items = [...form.line_items];
    items[index] = { ...items[index], [field]: value };
    setForm(prev => ({ ...prev, line_items: items }));
  };

  const addLineItem = () => setForm(prev => ({
    ...prev, line_items: [...prev.line_items, { _key: Date.now(), description: '', quantity: 1, unit_price: 0 }]
  }));

  const removeLineItem = (index) => {
    if (form.line_items.length <= 1) return;
    setForm(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== index) }));
  };

  const applyTemplate = (tpl) => {
    setForm(prev => ({
      ...prev,
      service_type: tpl.name,
      line_items: tpl.line_items.map(li => ({ ...li, _key: Date.now() + Math.random() })),
    }));
    setShowTemplates(false);
    toast.success(`${tpl.name} template applied`);
  };

  const addAddon = (addon) => {
    setForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { _key: Date.now() + Math.random(), description: addon.description, quantity: 1, unit_price: addon.unit_price }],
    }));
    toast.success(`Added: ${addon.description}`);
  };

  const subtotal = form.line_items.reduce((s, i) => s + (parseFloat(i.unit_price) || 0) * (parseInt(i.quantity) || 1), 0);
  const total = subtotal + (parseFloat(form.tax_amount) || 0);
  const chargeAmount = form.payment_type === 'deposit' ? total * (form.deposit_percentage / 100) : total;

  const canSubmit = form.client_name && form.client_email && form.line_items.some(i => i.description && i.unit_price > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (form.send_email && form.schedule_send) {
      if (!form.scheduled_send_at) {
        toast.error('Pick a date and time for the scheduled send');
        return;
      }
      if (new Date(form.scheduled_send_at).getTime() <= Date.now()) {
        toast.error('Scheduled send time must be in the future');
        return;
      }
    }
    setSubmitting(true);
    try {
      const scheduled = form.send_email && form.schedule_send && form.scheduled_send_at
        ? new Date(form.scheduled_send_at).toISOString()
        : null;
      const payload = {
        ...form,
        tax_amount: parseFloat(form.tax_amount) || 0,
        deposit_percentage: parseFloat(form.deposit_percentage) || 25,
        line_items: form.line_items.filter(i => i.description).map(i => ({
          description: i.description,
          quantity: parseInt(i.quantity) || 1,
          unit_price: parseFloat(i.unit_price) || 0,
        })),
        scheduled_send_at: scheduled,
      };
      // Strip the UI-only fields
      delete payload.send_email;
      delete payload.schedule_send;
      const created = await axios.post(`${API_URL}/api/admin-invoices`, payload, { headers });
      const invId = created.data?.id;
      if (form.send_email && !scheduled && invId) {
        try {
          await axios.post(`${API_URL}/api/admin-invoices/${invId}/send`, {}, { headers });
          toast.success(`Invoice created and emailed to ${form.client_email}`);
        } catch (sendErr) {
          toast.warning(`Invoice created — but email failed: ${sendErr.response?.data?.detail || sendErr.message}`);
        }
      } else if (scheduled) {
        const when = new Date(scheduled).toLocaleString();
        toast.success(`Invoice scheduled — will email on ${when}`);
      } else {
        toast.success('Invoice created as draft');
      }
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invoice');
    } finally { setSubmitting(false); }
  };

  const selectedTemplate = templates.find(t => t.name === form.service_type);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="create-invoice-form">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-create-form">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold text-zinc-900">Create Invoice</h1>
          </div>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}
            className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl" data-testid="submit-invoice-btn">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            {!form.send_email ? 'Save as Draft' : (form.schedule_send ? 'Schedule Send' : 'Create & Email')}
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Templates */}
        <Card className="border-zinc-100">
          <CardContent className="p-4">
            <button onClick={() => setShowTemplates(!showTemplates)}
              className="w-full flex items-center justify-between text-sm font-semibold text-zinc-700" data-testid="toggle-templates">
              <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#F56F05]" /> Service Templates</span>
              {showTemplates ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <AnimatePresence>
              {showTemplates && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                    {templates.map(tpl => (
                      <button key={tpl.id} onClick={() => applyTemplate(tpl)}
                        className={`text-left p-3 rounded-xl border-2 transition-all text-xs ${
                          form.service_type === tpl.name ? 'border-[#F56F05] bg-orange-50' : 'border-zinc-100 hover:border-zinc-200'
                        }`} data-testid={`template-${tpl.id}`}>
                        <p className="font-bold text-zinc-900 mb-0.5">{tpl.name}</p>
                        <p className="text-zinc-400">${tpl.line_items[0]?.unit_price?.toFixed(2)}</p>
                      </button>
                    ))}
                  </div>
                  {/* Show addons for selected template */}
                  {selectedTemplate?.addons?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-zinc-100">
                      <p className="text-xs font-semibold text-zinc-500 mb-2">Available Add-ons</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedTemplate.addons.map((addon, i) => (
                          <button key={addon.description} onClick={() => addAddon(addon)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-zinc-200 hover:border-[#F56F05]/40 transition-colors"
                            data-testid={`addon-${i}`}>
                            <Plus className="h-3 w-3 text-[#F56F05]" />
                            <span>{addon.description}</span>
                            <span className="text-[#F56F05] font-medium">${addon.unit_price}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>

        {/* Client Info */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 space-y-4">
            <p className="text-sm font-semibold text-zinc-700">Client Information</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Full Name *</Label>
                <Input value={form.client_name} onChange={e => set('client_name', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" placeholder="John Smith" data-testid="client-name" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Email *</Label>
                <Input type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" placeholder="john@example.com" data-testid="client-email" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Phone</Label>
                <Input value={form.client_phone} onChange={e => set('client_phone', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" placeholder="(503) 555-1234" data-testid="client-phone" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Property Address</Label>
                <Input value={form.property_address} onChange={e => set('property_address', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" placeholder="123 Main St, Portland OR" data-testid="property-address" />
              </div>
            </div>
            {referralDiscount && (
              <div className="mt-2 flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="referral-discount-banner">
                <Sparkles className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-emerald-800">
                    {referralDiscount.kind === 'welcome'
                      ? "This client was referred — 10% welcome discount available."
                      : "This client earned a referral reward — 10% off available."}
                  </p>
                  <p className="text-[11px] text-emerald-700">
                    Toggle below to apply it. We'll add a discount line item and consume the credit on save.
                  </p>
                </div>
                <label className="flex items-center gap-2 text-xs font-medium text-emerald-800 cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={form.apply_referral_discount}
                    onChange={e => set('apply_referral_discount', e.target.checked)}
                    className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    data-testid="apply-referral-discount-checkbox"
                  />
                  Apply 10% off
                </label>
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-zinc-100">
          <CardContent className="p-4 space-y-4">
            <p className="text-sm font-semibold text-zinc-700">Service Details</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Bedrooms</Label>
                <Input value={form.bedrooms} onChange={e => set('bedrooms', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" placeholder="3" data-testid="bedrooms" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Bathrooms</Label>
                <Input value={form.bathrooms} onChange={e => set('bathrooms', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" placeholder="2" data-testid="bathrooms" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Date of Service</Label>
                <Input type="date" value={form.date_of_service} onChange={e => set('date_of_service', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" data-testid="date-of-service" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Due Date</Label>
                <Input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" data-testid="due-date" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-zinc-700">Line Items</p>
              <Button variant="outline" size="sm" onClick={addLineItem} className="rounded-lg text-xs h-8" data-testid="add-line-item">
                <Plus className="h-3 w-3 mr-1" /> Add Item
              </Button>
            </div>
            {form.line_items.map((item, i) => (
              <div key={item._key} className="flex items-start gap-2" data-testid={`line-item-${i}`}>
                <div className="flex-1">
                  <Input value={item.description} onChange={e => updateLineItem(i, 'description', e.target.value)}
                    className="rounded-xl border-zinc-200 text-sm" placeholder="Description" data-testid={`line-desc-${i}`} />
                </div>
                <div className="w-16">
                  <Input type="number" min="1" value={item.quantity} onChange={e => updateLineItem(i, 'quantity', e.target.value)}
                    className="rounded-xl border-zinc-200 text-sm text-center" data-testid={`line-qty-${i}`} />
                </div>
                <div className="w-24">
                  <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={e => updateLineItem(i, 'unit_price', e.target.value)}
                    className="rounded-xl border-zinc-200 text-sm text-right" placeholder="0.00" data-testid={`line-price-${i}`} />
                </div>
                {form.line_items.length > 1 && (
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-zinc-400 hover:text-red-500" onClick={() => removeLineItem(i)}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}

            <Separator />

            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="font-medium">${subtotal.toFixed(2)}</span></div>
              <div className="flex items-center justify-between">
                <Label className="text-zinc-500 text-sm">Tax / Service Fee</Label>
                <Input type="number" min="0" step="0.01" value={form.tax_amount}
                  onChange={e => set('tax_amount', e.target.value)}
                  className="w-24 rounded-xl border-zinc-200 text-sm text-right h-8" data-testid="tax-amount" />
              </div>
              <Separator />
              <div className="flex justify-between font-bold text-base">
                <span>Total</span><span className="text-[#F56F05]">${total.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Type */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-semibold text-zinc-700">Payment Type</p>
            <div className="flex gap-3">
              {['full', 'deposit'].map(type => (
                <button key={type} onClick={() => set('payment_type', type)}
                  className={`flex-1 p-3 rounded-xl border-2 text-sm font-medium transition-all text-center ${
                    form.payment_type === type ? 'border-[#F56F05] bg-orange-50 text-[#F56F05]' : 'border-zinc-100 text-zinc-600'
                  }`} data-testid={`payment-type-${type}`}>
                  {type === 'full' ? 'Full Balance' : 'Deposit'}
                </button>
              ))}
            </div>
            {form.payment_type === 'deposit' && (
              <div className="flex items-center gap-3">
                <Label className="text-xs text-zinc-500 whitespace-nowrap">Deposit %</Label>
                <Input type="number" min="1" max="100" value={form.deposit_percentage}
                  onChange={e => set('deposit_percentage', e.target.value)}
                  className="w-20 rounded-xl border-zinc-200 text-sm text-center" data-testid="deposit-percentage" />
                <span className="text-sm font-bold text-[#F56F05]">= ${chargeAmount.toFixed(2)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 space-y-3">
            <div>
              <Label className="text-xs text-zinc-500">Notes (visible to client)</Label>
              <Textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" rows={2} placeholder="Any notes for the client..." data-testid="notes" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Internal Notes (admin only)</Label>
              <Textarea value={form.internal_notes} onChange={e => set('internal_notes', e.target.value)}
                className="mt-1 rounded-xl border-zinc-200" rows={2} placeholder="Internal notes..." data-testid="internal-notes" />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer" data-testid="send-email-toggle">
              <input
                type="checkbox"
                checked={form.send_email}
                onChange={e => set('send_email', e.target.checked)}
                className="h-4 w-4 rounded accent-[#F56F05]"
              />
              <span className="font-medium">Email this invoice to the client</span>
            </label>
            {form.send_email && (
              <div className="pl-6 space-y-3">
                <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer" data-testid="schedule-send-toggle">
                  <input
                    type="checkbox"
                    checked={form.schedule_send}
                    onChange={e => set('schedule_send', e.target.checked)}
                    className="h-4 w-4 rounded accent-[#F56F05]"
                  />
                  <Clock className="h-3.5 w-3.5 text-zinc-400" />
                  Schedule for later (instead of sending now)
                </label>
                {form.schedule_send && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-zinc-500">Send at *</Label>
                      <Input
                        type="datetime-local"
                        value={form.scheduled_send_at}
                        min={new Date(Date.now() + 60_000).toISOString().slice(0,16)}
                        onChange={e => set('scheduled_send_at', e.target.value)}
                        className="mt-1 rounded-xl border-zinc-200"
                        data-testid="scheduled-send-input"
                      />
                    </div>
                    <div className="flex items-end gap-2 flex-wrap">
                      {[
                        { label: 'Tomorrow 9 AM', mod: () => { const d = new Date(); d.setDate(d.getDate()+1); d.setHours(9,0,0,0); return d; } },
                        { label: 'Mon 9 AM', mod: () => { const d = new Date(); const days = ((1 - d.getDay()) + 7) % 7 || 7; d.setDate(d.getDate() + days); d.setHours(9,0,0,0); return d; } },
                        { label: '+1 hour', mod: () => { const d = new Date(); d.setHours(d.getHours()+1); d.setMinutes(0,0,0); return d; } },
                      ].map(p => (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => {
                            const d = p.mod();
                            const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0,16);
                            set('scheduled_send_at', local);
                          }}
                          className="text-[11px] px-2.5 py-1 rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition-colors"
                          data-testid={`preset-${p.label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="cancel-create">Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}
            className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl px-8" data-testid="submit-invoice-bottom">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            {!form.send_email ? 'Save as Draft' : (form.schedule_send ? 'Schedule Send' : 'Create & Email Invoice')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminInvoicesPage;
